/**
 * Draft SO line model + derivations (S2.2).
 *
 * The create/edit page owns a `DraftSoLine[]`; the editor renders it and
 * mutates via events. Everything money-derived is computed here with the
 * SAME rounding the resolver/handler use (`roundHalfUp2`) so the preview
 * numbers can never drift from what the mock API persists.
 */

import { evaluateMarginFloor, lineMarginComparable, roundHalfUp2 } from '@/lib/pricing';
import type { MarginFloorEvaluation } from '@/lib/pricing';
import type { PriceResolution } from '@/types/api/pricing';
import type { SalesOrderPriceHoldReason } from '@/types/api/salesOrder';
import { SO_PRICE_DEVIATION_THRESHOLD_PCT } from '@/types/api/salesOrder';

export interface DraftSoLine {
  key: string;
  productId: string | null;
  sku: string;
  productName: string;
  /** Auto-filled when the selected customer has a PN cross-ref. */
  customerPartNumber: string | null;
  qty: number;
  resolving: boolean;
  /** Resolver snapshot; becomes the line's persisted immutable priceTrace. */
  resolution: PriceResolution | null;
  /** Transacted price (resolved, or manually overridden with a reason). */
  unitPrice: number | null;
  overridden: boolean;
  overrideReason: string | null;
  /**
   * RULING-7 (2026-08-29): latest explicit re-resolve detach, queued on the
   * draft and flushed to the audit lens only at successful save (last detach
   * wins — final state audits, never draft churn). Cleared on product switch;
   * dies with a removed line.
   */
  detachedOverride: {
    qty: number;
    overriddenUnitPrice: number;
    overrideReason: string;
    resolvedUnitPrice: number | null;
  } | null;
}

let draftLineSeq = 0;
export function newDraftSoLine(): DraftSoLine {
  return {
    key: `draft-${Date.now()}-${++draftLineSeq}`,
    productId: null,
    sku: '',
    productName: '',
    customerPartNumber: null,
    qty: 1,
    resolving: false,
    resolution: null,
    unitPrice: null,
    overridden: false,
    overrideReason: null,
    detachedOverride: null,
  };
}

/** Landed-cost snapshot from the resolution waterfall; null when no cost record. */
export function draftLineLandedCost(line: DraftSoLine): number | null {
  return line.resolution?.waterfall.find((row) => row.key === 'landed_cost')?.amount ?? null;
}

/**
 * unitPrice − landedCost (Board D profit); null when either side is unknown —
 * and null when the price and the cost are in DIFFERENT currencies, because
 * their difference is not a number (2026-09-16: a USD order over an AUD cost
 * build-up printed a green "3.25 (25.49%)" that was neither USD nor a margin).
 */
export function draftLineMargin(
  line: DraftSoLine,
  /** The order's currency; omit on a single-currency surface. */
  currency?: string | null
): { amount: number; pct: number } | null {
  const landed = draftLineLandedCost(line);
  if (landed === null || line.unitPrice === null || line.unitPrice <= 0) return null;
  if (
    !lineMarginComparable({
      landedCost: landed,
      currency: currency ?? null,
      priceTrace: line.resolution,
    })
  ) {
    return null;
  }
  const amount = roundHalfUp2(line.unitPrice - landed);
  return { amount, pct: roundHalfUp2((amount / line.unitPrice) * 100) };
}

/** The currency the cost build-up behind this line is expressed in. */
export function draftLineCostCurrency(line: DraftSoLine): string | null {
  return line.resolution?.currency ?? null;
}

/** qty × unitPrice, 2dp; null while the price is unknown. */
export function draftLineTotal(line: DraftSoLine): number | null {
  if (line.unitPrice === null) return null;
  return roundHalfUp2(line.qty * line.unitPrice);
}

/** True when the transacted price deviates beyond the pending-approval gate. */
export function draftLineBeyondThreshold(line: DraftSoLine): boolean {
  const resolved = line.resolution?.unitPrice ?? null;
  if (resolved === null || resolved === 0 || line.unitPrice === null) return false;
  const pct = roundHalfUp2((Math.abs(line.unitPrice - resolved) / resolved) * 100);
  return pct > SO_PRICE_DEVIATION_THRESHOLD_PCT;
}

/**
 * Margin-floor evaluation of a draft line against the company minimum margin
 * (null ⇒ no floor ⇒ `no_floor`). Same pure rule the handler applies on save
 * (`rules/salesOrder.ts` → `marginFloor.ts`), so the tag the operator sees
 * while typing is the hold the API will raise.
 */
export function draftLineMarginFloor(
  line: DraftSoLine,
  floorPct: number | null,
  /** The order's currency — a margin is never compared across currencies. */
  currency?: string | null
): MarginFloorEvaluation {
  return evaluateMarginFloor({
    unitPrice: line.unitPrice,
    landedCost: draftLineLandedCost(line),
    floorPct,
    comparable: lineMarginComparable({
      landedCost: draftLineLandedCost(line),
      currency: currency ?? null,
      priceTrace: line.resolution,
    }),
  });
}

/** Every hold reason this draft line would raise on save, in the pinned order. */
export function draftLineHoldReasons(
  line: DraftSoLine,
  floorPct: number | null,
  currency?: string | null
): SalesOrderPriceHoldReason[] {
  const reasons: SalesOrderPriceHoldReason[] = [];
  if (draftLineBeyondThreshold(line)) reasons.push('deviation');
  if (draftLineMarginFloor(line, floorPct, currency).status === 'below_floor') {
    reasons.push('low_margin');
  }
  return reasons;
}
